import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../../services/api';
import {
  MachineActivity,
  ActivitiesResponse,
  ActivityFilters,
  CreateActivityRequest,
} from '../../types/activity';

interface ActivitiesState {
  items: MachineActivity[];
  currentActivity: MachineActivity | null;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  pagination: {
    page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
  };
}

const initialState: ActivitiesState = {
  items: [],
  currentActivity: null,
  isLoading: false,
  isCreating: false,
  error: null,
  pagination: {
    page: 1,
    per_page: 20,
    total_items: 0,
    total_pages: 0,
  },
};

// Fetch activities for a specific machine
export const fetchMachineActivities = createAsyncThunk<
  ActivitiesResponse,
  { machineId: string; limit?: number; offset?: number }
>('activities/fetchMachineActivities', async ({ machineId, limit = 20, offset = 0 }) => {
  const response = await apiClient.get(`/machines/${machineId}/activities`, {
    params: { limit, offset },
  });
  return response.data;
});

// Fetch all activities with filters
export const fetchActivities = createAsyncThunk<ActivitiesResponse, ActivityFilters | undefined>(
  'activities/fetchActivities',
  async (filters) => {
    const response = await apiClient.get('/activities', { params: filters });
    return response.data;
  }
);

// Get single activity
export const fetchActivity = createAsyncThunk<MachineActivity, string>(
  'activities/fetchActivity',
  async (id) => {
    const response = await apiClient.get(`/activities/${id}`);
    return response.data;
  }
);

// Create activity (admin only)
export const createActivity = createAsyncThunk<MachineActivity, CreateActivityRequest>(
  'activities/createActivity',
  async (data) => {
    const response = await apiClient.post('/activities', data);
    return response.data;
  }
);

// Delete activity (admin only)
export const deleteActivity = createAsyncThunk<void, string>(
  'activities/deleteActivity',
  async (id) => {
    await apiClient.delete(`/activities/${id}`);
  }
);

const activitiesSlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    clearActivities: (state) => {
      state.items = [];
      state.pagination = initialState.pagination;
    },
    clearCurrentActivity: (state) => {
      state.currentActivity = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch machine activities
    builder
      .addCase(fetchMachineActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMachineActivities.fulfilled, (state, action: PayloadAction<ActivitiesResponse>) => {
        state.isLoading = false;
        state.items = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_items: action.payload.total_items,
          total_pages: action.payload.total_pages,
        };
      })
      .addCase(fetchMachineActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch activities';
      });

    // Fetch all activities
    builder
      .addCase(fetchActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActivities.fulfilled, (state, action: PayloadAction<ActivitiesResponse>) => {
        state.isLoading = false;
        state.items = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_items: action.payload.total_items,
          total_pages: action.payload.total_pages,
        };
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch activities';
      });

    // Fetch single activity
    builder
      .addCase(fetchActivity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActivity.fulfilled, (state, action: PayloadAction<MachineActivity>) => {
        state.isLoading = false;
        state.currentActivity = action.payload;
      })
      .addCase(fetchActivity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch activity';
      });

    // Create activity
    builder
      .addCase(createActivity.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createActivity.fulfilled, (state, action: PayloadAction<MachineActivity>) => {
        state.isCreating = false;
        state.items.unshift(action.payload); // Add to beginning of list
        state.pagination.total_items += 1;
      })
      .addCase(createActivity.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create activity';
      });

    // Delete activity
    builder
      .addCase(deleteActivity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteActivity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter((activity) => activity.id !== action.meta.arg);
        state.pagination.total_items -= 1;
      })
      .addCase(deleteActivity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete activity';
      });
  },
});

export const { clearActivities, clearCurrentActivity, clearError } = activitiesSlice.actions;

export default activitiesSlice.reducer;
